import React, { useEffect, useState } from "react";
import { Alert, Spin, Button } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { FormAction } from "../../../types/enums";
import GETJwtToken from "../../../redux/authentication/authentication";
import { useLazyGetRoleQuery } from "../../../redux/roles/roleAPI";
import RolesCreateRoleForm from "./RolesCreateRoleForm";

/**
 * Dedicated full-page edit route — currently only reached for roles that
 * belong to the master organization (see RolesTable.tsx: master-org roles
 * skip the RolesEditRoleForm drawer and land here instead, after the
 * password+TOTP step-up challenge). Mirrors UsersEdit.tsx: fetch the role
 * by id from the URL, then hand it to the Create/Edit-unified form.
 */
export default function RolesEdit() {
  const navigate = useNavigate();
  const { id = "" } = useParams<{ id: string }>();
  const normalizedRoleId = decodeURIComponent(String(id ?? "")).trim();

  const [roleData, setRoleData] = useState<any>(null);
  const [loadError, setLoadError] = useState<string>("");
  // Bumped after every successful save so the form below remounts (via its
  // `key`) instead of navigating away — RolesCreateRoleForm only reads
  // `initialRole` once, on mount, so re-seeding its fields with the
  // freshly-saved role needs a fresh instance, not just a new prop value.
  const [refreshNonce, setRefreshNonce] = useState(0);

  const [triggerGetRole, { isLoading }] = useLazyGetRoleQuery();

  const loadRole = async () => {
    if (!normalizedRoleId || normalizedRoleId === "[object Object]") {
      setLoadError("Invalid role id");
      return;
    }

    try {
      const token = await GETJwtToken();
      const response = await triggerGetRole({ roleId: normalizedRoleId, token }).unwrap();
      const resolvedRole = response?.data ?? response;

      if (!resolvedRole) {
        setLoadError("Role not found");
        return;
      }

      setLoadError("");
      setRoleData(resolvedRole);
    } catch {
      setLoadError("Failed to load role data");
    }
  };

  useEffect(() => {
    loadRole();
  }, [normalizedRoleId, triggerGetRole]);

  // Editing a role stays on this page after Save — only re-fetches the role
  // so the form reflects what was just persisted, rather than navigating
  // back to the list the way Create does.
  const onSaveSuccessStay = async () => {
    await loadRole();
    setRefreshNonce((nonce) => nonce + 1);
  };

  if (isLoading) {
    return <Spin spinning tip="Loading" />;
  }

  if (loadError) {
    return (
      <Alert
        type="error"
        showIcon
        message="Edit role"
        description={loadError}
        action={
          <Button type="link" onClick={() => navigate("/organization-management/roles")}>
            Back to roles
          </Button>
        }
      />
    );
  }

  if (!roleData) {
    return null;
  }

  return (
    <RolesCreateRoleForm
      key={refreshNonce}
      mode={FormAction.Edit}
      roleId={normalizedRoleId}
      initialRole={roleData}
      onSaveSuccess={onSaveSuccessStay}
      onCancel={() => navigate("/organization-management/roles")}
    />
  );
}
